import React, { useState } from 'react';
import SectionedDialog from './SectionedDialog';

/** Re-mint a lite-node bundle for an EXISTING lite node.
 *
 * Opens from NodeDetail when the node is `detail.lite`. Mirrors the
 * lite-bundle picker in AddNodeModal — helpers preset + security
 * profile + output format + (optional) Wi-Fi block — but operates on
 * the existing node's credentials (no new node row, no new node_id).
 *
 * Why this exists separately from BuildInstallerModal:
 *   - BuildInstallerModal targets bin/build-installer.sh (standalone
 *     substrate flow with app-identity).
 *   - Lite bundles have no app-identity and are composed in-memory by
 *     the BFF (no subprocess, no installer-task polling).
 *
 * Behavior gotchas worth surfacing to the operator (in modal copy):
 *   - Switching profile lite ⇄ full mints a NEW X25519 keypair on
 *     `full` (key rotation — the operator is going to re-flash the
 *     device anyway). The old key is discarded.
 *   - Switching `full → lite` leaves the prior pubkey node_prop in
 *     place; harmless (upstream's LOOKUP_KEY no-ops on a lite-profile
 *     register). Documented so a reviewer doesn't expect a cleanup.
 *
 * The endpoint returns the bundle as a streamed download
 * (Content-Disposition: attachment) — we read it as a blob and trigger
 * a browser-side anchor-click download. */
export default function LiteBundleModal({ nodeId, nodeName, onClose }) {
  const [litePreset, setLitePreset] = useState('none');
  const [liteProfile, setLiteProfile] = useState('lite');
  const [outputFormat, setOutputFormat] = useState('run');
  const [picoWifiSsid, setPicoWifiSsid] = useState('');
  const [picoWifiPsk, setPicoWifiPsk] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [downloadedFname, setDownloadedFname] = useState(null);
  // admin-sectioned-dialogs: active category pane.
  const [activeCat, setActiveCat] = useState('bundle');

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);
    setError(null);
    setDownloadedFname(null);
    try {
      // kiosk-browser-display: kiosk = Pi-class CPython → force 'full'
      // (mirror of AddNodeModal's guard).
      const effectiveProfile = litePreset === 'microcontroller' ? 'lite'
        : litePreset === 'kiosk' ? 'full' : liteProfile;
      const body = {
        helpers_preset: litePreset,
        profile: effectiveProfile,
        output_format: outputFormat,
        wifi_ssid: picoWifiSsid.trim(),
        wifi_psk: picoWifiPsk,
      };
      const r = await fetch(`/api/nodes/${nodeId}/rebuild-lite-bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let parsed = null;
        try { parsed = await r.json(); } catch (_) {}
        const hint = (
          r.status === 404 ? ' (node not found in hub — was it deleted?)' :
          r.status === 502 ? ' (hub unreachable or pubkey persist failed; retry shortly)' :
          ''
        );
        throw new Error(
          (parsed && (parsed.message || parsed.error) || `HTTP ${r.status}`) + hint
        );
      }
      const blob = await r.blob();
      const fnamePrefix = litePreset === 'microcontroller' ? 'pico-node'
        : litePreset === 'kiosk' ? 'kiosk-node' : 'lite-node';
      const ext = outputFormat === 'zip' ? 'zip' : 'run';
      const fname = `${fnamePrefix}-${nodeId}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloadedFname(fname);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const renderBundle = () => (
    <>
            <label style={labelStyle}>
              <span>Helper bundle</span>
              <select value={litePreset} onChange={(e) => setLitePreset(e.target.value)}
                      style={inputStyle}>
                <option value="none">None — echo helper only (default)</option>
                <option value="microcontroller">Pico starter kit — sensor + wifi-status helpers</option>
                <option value="weather">Weather — forecast + comfort helper (CPython lite)</option>
                <option value="kiosk">Kiosk display — browser second screen (Pi + Chromium)</option>
              </select>
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                {litePreset === 'microcontroller'
                  ? 'Bundle ships sensor + wifi-status helpers; config.json gets a wifi block populated below.'
                  : litePreset === 'kiosk'
                    ? 'Browser-kiosk display node: supervised Chromium showing the live station output. Needs Chromium + a graphical session on the device.'
                    : 'Bundle ships only the runtime + echo demo helper; recipient has wired/managed connectivity.'}
              </small>
            </label>

            <label style={labelStyle}>
              <span>Security profile</span>
              <select value={litePreset === 'microcontroller' ? 'lite' : liteProfile}
                      onChange={(e) => setLiteProfile(e.target.value)}
                      disabled={litePreset === 'microcontroller'}
                      style={inputStyle}>
                <option value="lite">Lite — HMAC handshake + plaintext envelope</option>
                <option value="full">Full — X25519 ECDH handshake + AEAD envelope</option>
              </select>
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                {litePreset === 'microcontroller'
                  ? 'Forced to lite — Pico hardware (RP2350 / MicroPython) can\'t run ECDH/AEAD in the runtime budget.'
                  : (liteProfile === 'full'
                      ? 'Re-mint rotates the X25519 keypair: aveq-admin mints a fresh one, the private key rides in this bundle\'s config.json, the new public key is published to the hub before download.'
                      : 'Plaintext envelope with CRC32 integrity. Any prior `full`-profile pubkey on the hub is left in place but unused (harmless).')}
              </small>
            </label>

            <label style={labelStyle}>
              <span>Output format</span>
              <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)}
                      style={inputStyle}>
                <option value="run">.run — self-extracting bash (preferred)</option>
                <option value="zip">.zip — raw archive (legacy)</option>
              </select>
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                <code>.run</code> wraps install/run helpers around the bundle so the recipient
                runs one file. <code>.zip</code> kept for operator scripts that grep filenames.
              </small>
            </label>
    </>
  );

  const renderWifi = () => (
    <>
                <label style={labelStyle}>
                  <span>Wi-Fi SSID (optional)</span>
                  <input value={picoWifiSsid} onChange={(e) => setPicoWifiSsid(e.target.value)}
                         placeholder="my-network" style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  <span>Wi-Fi password (optional)</span>
                  <input value={picoWifiPsk} onChange={(e) => setPicoWifiPsk(e.target.value)}
                         placeholder="" type="password" style={inputStyle} />
                  <small style={{ color: '#888', fontSize: '0.75rem' }}>
                    Stamped into the bundle's <code>config.json</code>. Leave blank to edit on
                    the device after flashing.
                  </small>
                </label>
    </>
  );

  const categories = [
    { id: 'bundle', label: 'Bundle', description: `Re-mints the bundle for ${nodeName || `node ${nodeId}`} using its existing credentials — no new node id.`, render: renderBundle },
    { id: 'wifi', label: 'Wi-Fi', description: 'Wi-Fi credentials stamped into the bundle config.json.', applicable: litePreset === 'microcontroller', render: renderWifi },
  ];

  const resultNode = downloadedFname ? (
    <p style={{ color: '#444', fontSize: '0.9rem' }}>
      Bundle <code>{downloadedFname}</code> downloaded.
      {liteProfile === 'full' && litePreset !== 'microcontroller' && (
        <> The hub&apos;s pubkey for this node was rotated to the new keypair —
        if you cancel re-flashing, the device will keep using its old privkey
        and fail the next handshake until a fresh bundle is re-minted.</>
      )}
    </p>
  ) : null;

  const resultFooter = (
    <button type="button" onClick={onClose} style={buttonPrimaryStyle}>Done</button>
  );

  return (
    <SectionedDialog
      title="Build lite bundle"
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      primaryLabel="Build bundle"
      submittingLabel="Building…"
      submitting={submitting}
      onPrimary={submit}
      onCancel={onClose}
      error={error}
      result={resultNode}
      resultFooter={resultFooter}
    />
  );
}

const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const buttonPrimaryStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
